import customEventHandler from './customEvents';
// Shared logic for the "Vertical Pieces" / "Horizontal Pieces" single-add
// action. Used by BOTH the right tool panel (BottomPanel.tsx) and the
// ColoredTiles context menu (ContextMenu.tsx) so the two entry points behave
// identically: place one LinearPiece at the first free edge slot of the
// selected tile (vertical → left then right; horizontal → top then bottom),
// or, when every edge slot is full, in an expanding orphan ring around the
// tile. The actual snap/creation is done by the `createLinearPiece` handler in
// ColoredTileController.tsx — this only computes the target coordinates.
// Returns true when a LinearPiece of the given orientation already sits at
// (x, y) within half a cell — i.e. a new piece there would overlap an existing.
var positionOccupiedByLinearPiece = function (manipulative, x, y, orientation, cellSize) {
    var tol = cellSize / 2;
    return manipulative.some(function (item) {
        if (item.data.type !== 'linearPiece')
            return false;
        var lp = item.data;
        if (lp.orientation !== orientation)
            return false;
        return Math.abs(lp.center.x - x) < tol && Math.abs(lp.center.y - y) < tol;
    });
};
// Find the next free orphan-ring position around the tile. Each ring is one
// cellSize further out than the previous. Within a ring, vertical pieces fill
// the right column first, then left; horizontal pieces fill the bottom row
// first, then top — so successive overflow clicks wrap around the tile in
// expanding rings instead of trailing off in one direction.
var findRingPosition = function (manipulative, orientation, tileLeft, tileRight, tileTop, tileBottom, perimeterLength, cellSize) {
    var MAX_LAYERS = 100; // safety cap
    for (var layer = 1; layer <= MAX_LAYERS; layer++) {
        if (orientation === 'vertical') {
            // Ring sides for vertical pieces: right column then left column.
            for (var _i = 0, _a = ['right', 'left']; _i < _a.length; _i++) {
                var side = _a[_i];
                var x = side === 'right' ? tileRight + layer * cellSize : tileLeft - layer * cellSize;
                for (var row = 0; row < perimeterLength; row++) {
                    var y = tileTop + row * cellSize + cellSize / 2;
                    if (!positionOccupiedByLinearPiece(manipulative, x, y, 'vertical', cellSize)) {
                        return { x: x, y: y, layer: layer, side: side };
                    }
                }
            }
        }
        else {
            // Horizontal pieces: bottom row then top row.
            for (var _b = 0, _c = ['bottom', 'top']; _b < _c.length; _b++) {
                var side = _c[_b];
                var y = side === 'bottom' ? tileBottom + layer * cellSize : tileTop - layer * cellSize;
                for (var col = 0; col < perimeterLength; col++) {
                    var x = tileLeft + col * cellSize + cellSize / 2;
                    if (!positionOccupiedByLinearPiece(manipulative, x, y, 'horizontal', cellSize)) {
                        return { x: x, y: y, layer: layer, side: side };
                    }
                }
            }
        }
    }
    // Fallback (should never hit at 100 layers × 2 sides × perimeter): drop on
    // the right/below first slot, may overlap an existing piece.
    if (orientation === 'vertical') {
        return {
            x: tileRight + cellSize,
            y: tileTop + cellSize / 2,
            layer: MAX_LAYERS + 1,
            side: 'right',
        };
    }
    return {
        x: tileLeft + cellSize / 2,
        y: tileBottom + cellSize,
        layer: MAX_LAYERS + 1,
        side: 'bottom',
    };
};
// Resolve the ColoredTiles tile a context-menu target acts on:
//  - a `coloredTiles_` id (not a cell) → that tile,
//  - a `manipulativeGroup_` overlay → the first colored tile in the group,
//  - a `linearPiece_` id → the tile it is snapped to (else the nearest tile).
// Lets the CT-group and LinearPiece context menus expose the same controls the
// right panel does (the right panel resolves the same tile). Returns null when
// no colored tile is associated (e.g. cell sub-menus, non-CT groups).
export var resolveColoredTilesTarget = function (manipId, manipulative) {
    var _a, _b;
    if (!manipId || manipId.includes('_cell_'))
        return null;
    if (manipId.startsWith('coloredTiles_')) {
        return (_a = manipulative.find(function (m) { return m.data.id === manipId && m.data.type === 'coloredTiles'; })) !== null && _a !== void 0 ? _a : null;
    }
    if (manipId.startsWith('manipulativeGroup_')) {
        return ((_b = manipulative.find(function (m) { return m.data.type === 'coloredTiles' && m.data.groupId === manipId; })) !== null && _b !== void 0 ? _b : null);
    }
    if (manipId.startsWith('linearPiece_')) {
        // Snapped parent tile (the piece id sits in one of the tile's snap arrays).
        for (var _i = 0, manipulative_1 = manipulative; _i < manipulative_1.length; _i++) {
            var item = manipulative_1[_i];
            if (item.data.type !== 'coloredTiles')
                continue;
            var td = item.data;
            var arrays = [
                td.snappedVerticalPieceIds,
                td.snappedRightVerticalPieceIds,
                td.snappedHorizontalPieceIds,
                td.snappedBottomHorizontalPieceIds,
            ];
            if (arrays.some(function (a) { return a === null || a === void 0 ? void 0 : a.includes(manipId); }))
                return item;
        }
        // Orphan piece → nearest tile by centre distance.
        var lp = manipulative.find(function (m) { return m.data.id === manipId; });
        if (lp && lp.data.type === 'linearPiece') {
            var lpData = lp.data;
            var best = null;
            var bestDist = Infinity;
            for (var _c = 0, manipulative_2 = manipulative; _c < manipulative_2.length; _c++) {
                var item = manipulative_2[_c];
                if (item.data.type !== 'coloredTiles')
                    continue;
                var td = item.data;
                var dist = Math.hypot(lpData.center.x - td.center.x, lpData.center.y - td.center.y);
                if (dist < bestDist) {
                    bestDist = dist;
                    best = item;
                }
            }
            return best;
        }
    }
    return null;
};
// Place a new LinearPiece at the appropriate edge of the selected ColoredTiles
// tile. Resolves the tile from `selectedTool` (direct tile selection, a
// selected LinearPiece's parent tile, or a manipulativeGroup containing a
// tile), computes the first free edge slot (or ring position), and dispatches
// `createLinearPiece`. Returns true if a piece was placed, false if no tile
// could be resolved (caller can fall through to drag-only behaviour).
export var createLinearPieceForSelectedTile = function (toolId, manipulative, selectedTool) {
    var _a, _b, _c, _d, _e;
    // 1. Direct ColoredTiles selection.
    var resolvedTile = manipulative.find(function (m) { var _a; return m.data.type === 'coloredTiles' && selectedTool.includes((_a = m.data.id) !== null && _a !== void 0 ? _a : ''); });
    // 2. Selection is a LinearPiece — find its parent tile (via snap arrays,
    //    then nearest-tile fallback for orphan pieces).
    if (!resolvedTile) {
        var selectedLinearPieceId_1 = selectedTool.find(function (id) { return id.startsWith('linearPiece_'); });
        if (selectedLinearPieceId_1) {
            for (var _i = 0, manipulative_3 = manipulative; _i < manipulative_3.length; _i++) {
                var item = manipulative_3[_i];
                if (item.data.type !== 'coloredTiles')
                    continue;
                var td_1 = item.data;
                var snapArrays = [
                    td_1.snappedVerticalPieceIds,
                    td_1.snappedRightVerticalPieceIds,
                    td_1.snappedHorizontalPieceIds,
                    td_1.snappedBottomHorizontalPieceIds,
                ];
                if (snapArrays.some(function (arr) { return arr === null || arr === void 0 ? void 0 : arr.includes(selectedLinearPieceId_1); })) {
                    resolvedTile = item;
                    break;
                }
            }
            if (!resolvedTile) {
                var lp = manipulative.find(function (m) { return m.data.id === selectedLinearPieceId_1; });
                if (lp && lp.data.type === 'linearPiece') {
                    var lpData = lp.data;
                    var closestDist = Infinity;
                    for (var _f = 0, manipulative_4 = manipulative; _f < manipulative_4.length; _f++) {
                        var item = manipulative_4[_f];
                        if (item.data.type !== 'coloredTiles')
                            continue;
                        var tdc = item.data;
                        var dist = Math.hypot(lpData.center.x - tdc.center.x, lpData.center.y - tdc.center.y);
                        if (dist < closestDist) {
                            closestDist = dist;
                            resolvedTile = item;
                        }
                    }
                }
            }
        }
    }
    // 3. Selection is a manipulativeGroup containing a coloredTile.
    if (!resolvedTile) {
        var groupId_1 = selectedTool.find(function (id) { return id.startsWith('manipulativeGroup_'); });
        if (groupId_1) {
            resolvedTile = manipulative.find(function (m) { return m.data.type === 'coloredTiles' && m.data.groupId === groupId_1; });
        }
    }
    if (!resolvedTile || resolvedTile.data.type !== 'coloredTiles') {
        return false;
    }
    var td = resolvedTile.data;
    var tileScale = (_a = td.scale) !== null && _a !== void 0 ? _a : 1;
    var cellSize = 110 * tileScale;
    var w = td.columns * cellSize;
    var h = td.rows * cellSize;
    var isHorizontal = toolId === 'horizontalPieces';
    var tileLeft = td.center.x - w / 2;
    var tileRight = td.center.x + w / 2;
    var tileTop = td.center.y - h / 2;
    var tileBottom = td.center.y + h / 2;
    var targetX;
    var targetY;
    // Treats both undefined entries AND missing indices as "free slot".
    var firstFreeSlot = function (arr, length) {
        for (var i = 0; i < length; i++) {
            if (!arr[i])
                return i;
        }
        return -1;
    };
    if (isHorizontal) {
        var top_1 = (_b = td.snappedHorizontalPieceIds) !== null && _b !== void 0 ? _b : [];
        var bottom = (_c = td.snappedBottomHorizontalPieceIds) !== null && _c !== void 0 ? _c : [];
        var slot = firstFreeSlot(top_1, td.columns);
        var onBottom = false;
        if (slot === -1) {
            slot = firstFreeSlot(bottom, td.columns);
            onBottom = true;
        }
        if (slot !== -1) {
            targetX = tileLeft + slot * cellSize + cellSize / 2;
            targetY = onBottom ? tileBottom : tileTop;
        }
        else {
            var pos = findRingPosition(manipulative, 'horizontal', tileLeft, tileRight, tileTop, tileBottom, td.columns, cellSize);
            targetX = pos.x;
            targetY = pos.y;
        }
    }
    else {
        var left = (_d = td.snappedVerticalPieceIds) !== null && _d !== void 0 ? _d : [];
        var right = (_e = td.snappedRightVerticalPieceIds) !== null && _e !== void 0 ? _e : [];
        var slot = firstFreeSlot(left, td.rows);
        var onRight = false;
        if (slot === -1) {
            slot = firstFreeSlot(right, td.rows);
            onRight = true;
        }
        if (slot !== -1) {
            targetX = onRight ? tileRight : tileLeft;
            targetY = tileTop + slot * cellSize + cellSize / 2;
        }
        else {
            var pos = findRingPosition(manipulative, 'vertical', tileLeft, tileRight, tileTop, tileBottom, td.rows, cellSize);
            targetX = pos.x;
            targetY = pos.y;
        }
    }
    customEventHandler.dispatchEvent('bottomPanel', {
        id: 'createLinearPiece',
        data: {
            type: isHorizontal ? 'horizontal' : 'vertical',
            x: targetX,
            y: targetY,
        },
    });
    return true;
};
